import React from 'react';
import { Gauge } from 'lucide-react';
import { computeAttribution, computeConfidence, STAGE_COLORS } from '../../lib/valueConversion';

/**
 * KpiConversionInsight — rendered under each comparable KPI row in the
 * Value Conversion Breakdown:
 *  - Modeled projection-confidence badge (R9) with off-course
 *    classification: "navigation failure" vs "projection risk".
 *  - Missing Middle attribution (R3): primary contributors to the
 *    conversion failure, derived from live journey-stage deficits.
 */
export function KpiConversionInsight({ kpi, stages }) {
  if (!kpi.has_projection || !kpi.has_actual) return null;
  const conf = computeConfidence(kpi);
  const attribution = (kpi.gap ?? 0) < 0 ? computeAttribution(kpi, stages) : null;

  return (
    <div className="mt-2 pt-2 border-t border-white/60 space-y-2" data-testid={`kpi-conversion-insight-${kpi.kpi_id}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span
          className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 py-0.5 font-medium text-slate-600"
          title="Modeled from data depth and recency — more data points and fresher actuals raise confidence"
          data-testid={`kpi-confidence-${kpi.kpi_id}`}
        >
          <Gauge className="h-3 w-3 text-cyan-700" />
          Projection confidence {conf.confidence}%
          <span className="text-slate-400">· modeled{conf.stale ? ' · stale data' : ''}</span>
        </span>
        {conf.classification === 'navigation_failure' && (
          <span className="inline-flex items-center rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 font-medium text-rose-700">
            High-confidence projection missed — navigation failure likely
          </span>
        )}
        {conf.classification === 'projection_risk' && (
          <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 font-medium text-amber-700">
            Low-signal projection — prediction risk, validate the baseline
          </span>
        )}
      </div>

      {attribution && (
        <div data-testid={`kpi-attribution-${kpi.kpi_id}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
            Primary contributors · modeled from journey-stage scores
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
            {attribution.contributors.map((c) => (
              <div key={c.key} className="rounded bg-white border border-slate-200 px-2 py-1">
                <div className="flex items-center justify-between text-[11px] text-slate-600">
                  <span className="truncate">{c.label}</span>
                  <span className="font-bold tabular-nums">{c.pct}%</span>
                </div>
                <div className="h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${c.pct}%`, backgroundColor: STAGE_COLORS[c.key] }}
                  />
                </div>
              </div>
            ))}
            <div className="rounded bg-slate-50 border border-slate-200 px-2 py-1">
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span className="truncate">External Factors</span>
                <span className="font-bold tabular-nums">{attribution.external}%</span>
              </div>
              <div className="h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
                <div className="h-full rounded-full bg-slate-400" style={{ width: `${attribution.external}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
